import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useReaderFunnel } from "../contexts/ReaderFunnelContext";
import { useNotification } from "../contexts/NotificationContext";
import ReaderNav from "../components/ReaderNav";
import ReaderMobileBottomNav from "../components/ReaderMobileBottomNav";
import UniversalFooter from "../components/UniversalFooter";
import NewTitlesDropdown from "../components/NewTitlesDropdown";
import ReadFreeDropdown from "../components/ReadFreeDropdown";
import ContinueReadingModal from "../components/ContinueReadingModal";
import CategoryDropdown from "../components/CategoryDropdown";
import PersonalizedDropdown from "../components/PersonalizedDropdown";
import OnboardingModal from "../components/OnboardingModal";
import { AskAudiaHelpModal, CONTEXT_CONFIG } from "../components/AskAudiaHelpButton";
import { BookOpen, Library } from "lucide-react";
import { useReaderCredits } from "../contexts/ReaderCreditContext";
import { BACKEND_URL as backendUrl } from '@/config/api';

const ReaderHomePage = () => {
  const { user, logout, getHomePage, activeRole } = useAuth();
  const { showOnboardingModal, setShowCreditExpiredModal, setShowPricingModal, isBetaTester } = useReaderFunnel();
  const { balance } = useReaderCredits();
  const { notifyBilling } = useNotification();
  const navigate = useNavigate();
  
  // Check if user is a "pure" reader (only has reader role, not admin/author/publisher)
  const userRoles = user?.roles || [];
  const hasPrivilegedRole = userRoles.includes('admin') || userRoles.includes('author') || 
                            userRoles.includes('publisher') || userRoles.includes('creator');
  const isPureReader = userRoles.includes('reader') && !hasPrivilegedRole;
  
  // Check if onboarding is complete - only applies to pure readers
  // Multi-role users (publisher+reader, admin+reader) skip onboarding in Reader mode
  const onboardingStatus = user?.onboarding_status;
  const needsOnboarding = isPureReader && (onboardingStatus === 'pending' || onboardingStatus === 'in_progress');

  // State for dashboard data
  const [dashboardData, setDashboardData] = useState(null);
  const [libraryBooks, setLibraryBooks] = useState([]);
  const [featuredBooks, setFeaturedBooks] = useState([]);
  const [newTitles, setNewTitles] = useState([]);
  const [newTitlesSectionName, setNewTitlesSectionName] = useState("New Titles");
  const [newTitlesSectionVisible, setNewTitlesSectionVisible] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showContinueReadingModal, setShowContinueReadingModal] = useState(false);
  const [showAudiaHelpModal, setShowAudiaHelpModal] = useState(false);
  
  // Accordion state: tracks which section is expanded (null = all collapsed)
  // Valid values: 'new-titles', 'personalized', 'read-free', 'title', 'author', 'genre', 'theme', or null
  const [expandedSection, setExpandedSection] = useState(null);
  
  // Handler for accordion toggle - collapses other sections when one expands
  const handleSectionToggle = (sectionId) => {
    setExpandedSection(prev => prev === sectionId ? null : sectionId);
  };

  // Handle resume listening - same logic as Library page
  const handleResumeListening = async (manuscriptId) => {
    // CREDIT GATE: Block playback if balance is 0 or less (beta testers exempt)
    if (!isBetaTester && typeof balance === 'number' && balance <= 0) {
      console.log('[HOME RESUME] BLOCKED: No credits remaining (balance:', balance, ')');
      notifyBilling('You need Reader Credits to listen. Please choose a plan to continue.');
      setShowCreditExpiredModal(true);
      return;
    }
    
    try {
      const token = localStorage.getItem('accessToken');
      // backendUrl imported from centralized config as BACKEND_URL
      
      // Get bookmark from localStorage (single source of truth)
      let userId = 'anonymous';
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          userId = payload.user_id || payload.sub || 'anonymous';
        } catch (e) {
          console.warn('[RESUME] Could not extract user ID from token');
        }
      }
      
      const bookmarkKey = `newaudia_bookmark_${userId}_${manuscriptId}`;
      const storedBookmark = localStorage.getItem(bookmarkKey);
      
      let bookmark = null;
      if (storedBookmark) {
        try {
          bookmark = JSON.parse(storedBookmark);
          console.log('[RESUME] Using bookmark from localStorage:', bookmark);
        } catch (e) {
          console.error('[RESUME] Error parsing bookmark:', e);
        }
      }
      
      // Create a session for this manuscript
      const response = await fetch(`${backendUrl}/api/manuscripts/${manuscriptId}/open`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to open manuscript');
      }
      
      const data = await response.json();
      const { session_id } = data;
      
      // Navigate to player with bookmark data and showRecapPrompt
      // If we have a bookmark, show the resume modal, otherwise just autoplay
      navigate(`/player/${session_id}`, {
        state: {
          returnTo: '/reader/my-library',  // Return to My Library when closing
          showRecapPrompt: bookmark ? true : false,  // Show resume modal only if bookmark exists
          bookmark: bookmark  // Pass the bookmark data if available
        }
      });
    } catch (error) {
      console.error('[RESUME] Error opening manuscript:', error);
      alert('Failed to resume listening. Please try again.');
    }
  };

  // Fetch dashboard data on mount - PARALLEL for faster loading
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('accessToken');
        // backendUrl imported from centralized config as BACKEND_URL
        const headers = { 'Authorization': `Bearer ${token}` };

        // Fire ALL requests in parallel for faster page load
        const [dashboardResponse, libraryResponse, featuredResponse, newTitlesResponse] = await Promise.all([
          fetch(`${backendUrl}/api/reader/dashboard`, { headers }),
          fetch(`${backendUrl}/api/reader/library`, { headers }),
          fetch(`${backendUrl}/api/reader/featured-titles`, { headers }),
          fetch(`${backendUrl}/api/reader/new-titles`, { headers })
        ]);

        // Process responses (order doesn't matter, all run in parallel)
        if (dashboardResponse.ok) {
          const dashboard = await dashboardResponse.json();
          setDashboardData(dashboard);
        } else {
          throw new Error('Failed to fetch dashboard data');
        }

        if (libraryResponse.ok) {
          const library = await libraryResponse.json();
          setLibraryBooks(library);
        }

        if (featuredResponse.ok) {
          const featured = await featuredResponse.json();
          setFeaturedBooks(featured);
        }

        if (newTitlesResponse.ok) {
          const titlesData = await newTitlesResponse.json();
          // Handle new response format with section_name and section_visible
          if (titlesData.section_visible !== undefined) {
            setNewTitles(titlesData.books || []);
            setNewTitlesSectionName(titlesData.section_name || "New Titles");
            setNewTitlesSectionVisible(titlesData.section_visible);
          } else {
            // Backwards compatibility: old format was just an array
            setNewTitles(titlesData);
          }
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Derived values from dashboard data
  const continueListeningBooks = dashboardData?.continue_listening || [];
  const hasLastSession = continueListeningBooks.length > 0;
  const stats = dashboardData?.stats || {
    total_books_in_library: 0,
    total_minutes_listened: 0,
    total_engagement_units: 0,
    passive_engagement_units: 0,
    active_engagement_units: 0,
    total_audia_questions: 0,
    total_words_consumed: 0,
    credits: { total: 0, active: 0, passive: 0, version_id: '' }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleAddToLibrary = async (manuscriptId) => {
    try {
      const token = localStorage.getItem('accessToken');
      // backendUrl imported from centralized config as BACKEND_URL
      
      const response = await fetch(`${backendUrl}/api/reader/library`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ manuscript_id: manuscriptId })
      });

      if (response.ok) {
        // Navigate to My Library page
        navigate('/reader/my-library');
      } else {
        alert('Failed to add book to library');
      }
    } catch (error) {
      console.error('Error adding to library:', error);
      alert('Error adding book to library');
    }
  };

  const handleReadNow = async (manuscriptId) => {
    // CREDIT GATE: Block playback if balance is 0 or less (beta testers exempt)
    if (!isBetaTester && typeof balance === 'number' && balance <= 0) {
      console.log('[HOME] BLOCKED: No credits remaining (balance:', balance, ')');
      notifyBilling('You need Reader Credits to listen. Please choose a plan to continue.');
      setShowCreditExpiredModal(true);
      return;
    }
    
    try {
      const token = localStorage.getItem('accessToken');
      // backendUrl imported from centralized config as BACKEND_URL
      
      // First add to library
      const addResponse = await fetch(`${backendUrl}/api/reader/library`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ manuscript_id: manuscriptId })
      });

      if (!addResponse.ok) {
        alert('Failed to add book to library');
        return;
      }

      // Create a new session starting at segment 0
      const sessionResponse = await fetch(`${backendUrl}/api/manuscripts/${manuscriptId}/open`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!sessionResponse.ok) {
        alert('Failed to open manuscript');
        return;
      }

      const sessionData = await sessionResponse.json();
      const { session_id } = sessionData;

      // Navigate to player starting at segment 0
      navigate(`/player/${session_id}`, {
        state: {
          returnTo: '/home',
          startFromBeginning: true  // Signal to start from segment 0
        }
      });
    } catch (error) {
      console.error('Error starting reading:', error);
      alert('Error starting reading');
    }
  };

  // If user needs onboarding, show a loading state while onboarding modal is displayed
  if (needsOnboarding) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-purple-500 border-t-transparent mx-auto mb-4" />
          <p className="text-slate-400">Setting up your experience...</p>
        </div>
        <OnboardingModal />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50 pb-20 md:pb-0 overflow-x-hidden">
      {/* Reader Mode Navigation - Logo, Credits, Buy Credits, Profile */}
      <ReaderNav />

      {/* Top gradient welcome header */}
      <header className="bg-gradient-to-br from-indigo-600 via-purple-600 to-sky-500">
        <div className="mx-auto max-w-6xl px-4 py-6 sm:py-8">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-1">
                NewAudia<sup className="text-lg sm:text-xl">™</sup>
              </p>
              <p className="text-2xl uppercase tracking-[0.2em] text-indigo-100">
                Reader Dashboard
              </p>
              <h1 className="mt-2 text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
                Welcome{user?.name ? `, ${user.name}` : ""}.
              </h1>
              <p className="mt-3 max-w-xl text-sm sm:text-base text-indigo-100/90">
                Your personal smart library is ready. Pick up where you left off,
                explore new titles, and let NewAudia do the heavy lifting while you listen.
                <Link to="/help/getting-started/what-is-newaudia" className="ml-2 text-indigo-200 hover:text-white underline underline-offset-2">
                  New here?
                </Link>
              </p>
              
              {/* Member Since + Quick Action Buttons */}
              <div className="mt-4 space-y-3">
                {dashboardData?.user?.created_at && (
                  <p className="text-lg font-semibold text-indigo-100">
                    NewAudia Member Since {new Date(dashboardData.user.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </p>
                )}
                
                {/* Continue Listening & My Library Buttons */}
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    onClick={() => setShowContinueReadingModal(true)}
                    className="flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-700 to-blue-800 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl shadow-md hover:shadow-lg transition-all duration-200 border border-blue-500/30"
                    data-testid="continue-reading-btn"
                  >
                    <BookOpen className="w-5 h-5" />
                    <span className="text-sm font-semibold">Continue Listening</span>
                  </button>
                  <Link
                    to="/reader/my-library"
                    className="flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-500 hover:to-emerald-600 text-white rounded-xl shadow-md hover:shadow-lg transition-all duration-200 border border-emerald-400/30"
                    data-testid="my-library-btn"
                  >
                    <Library className="w-5 h-5" />
                    <span className="text-sm font-semibold">My Library</span>
                  </Link>
                  {(user?.roles?.includes("author") || user?.roles?.includes("creator")) && (
                    <span className="inline-flex items-center rounded-full bg-emerald-900/50 px-3 py-1 text-xs font-medium text-emerald-100 ring-1 ring-emerald-200/40">
                      Author tools enabled
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Ask Audia Feature Card */}
            <div className="mt-6 w-full max-w-sm sm:mt-0">
              {/* Need Help Card */}
              <div className="rounded-2xl bg-gradient-to-br from-cyan-900/40 to-slate-950/60 p-4 backdrop-blur-sm border border-cyan-500/20">
                <div className="flex items-center gap-3 mb-3">
                  {/* Audia Avatar Image */}
                  <div className="relative flex-shrink-0">
                    <img 
                      src="/audia-avatar.jpg" 
                      alt="Audia - Your AI Reading Companion" 
                      className="w-14 h-14 rounded-full border-2 border-cyan-400/60 shadow-lg shadow-cyan-500/30 object-cover"
                    />
                    <span className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-emerald-400 rounded-full border-2 border-slate-900 animate-pulse"></span>
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-white">Need Help?</h2>
                    <p className="text-sm text-cyan-200 font-semibold">Ask Audia</p>
                  </div>
                </div>
                
                <p className="text-xs text-slate-300 mb-3 leading-relaxed">
                  Questions about NewAudia? I can help with navigation, features, Reader Credits, and more.
                </p>
                
                <button
                  onClick={() => setShowAudiaHelpModal(true)}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white text-sm font-semibold rounded-xl shadow-lg shadow-cyan-500/20 transition-all duration-200 hover:shadow-cyan-500/40"
                  data-testid="ask-audia-card-btn"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                  Chat with Audia
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="mx-auto max-w-6xl px-4 pb-16 pt-8 sm:pt-10">
        <div className="space-y-6">
          {/* New Titles / Recently Added Dropdown - Dynamic based on activation dates */}
          <NewTitlesDropdown 
            books={newTitles} 
            sectionName={newTitlesSectionName}
            sectionVisible={newTitlesSectionVisible}
            onAddToLibrary={handleAddToLibrary}
            onReadNow={handleReadNow}
            isExpanded={expandedSection === 'new-titles'}
            onToggleExpand={() => handleSectionToggle('new-titles')}
          />
          
          {/* Personalized Recommendations Dropdown - Based on onboarding preferences */}
          <PersonalizedDropdown
            onAddToLibrary={handleAddToLibrary}
            onReadNow={handleReadNow}
            isExpanded={expandedSection === 'personalized'}
            onToggleExpand={() => handleSectionToggle('personalized')}
          />
          
          {/* Read Free Dropdown */}
          <ReadFreeDropdown
            onAddToLibrary={handleAddToLibrary}
            onReadNow={handleReadNow}
            isExpanded={expandedSection === 'read-free'}
            onToggleExpand={() => handleSectionToggle('read-free')}
          />
          
          {/* Title Dropdown with Category Select */}
          <CategoryDropdown
            title="Browse by Title"
            filterType="title"
            onAddToLibrary={handleAddToLibrary}
            onReadNow={handleReadNow}
            isExpanded={expandedSection === 'title'}
            onToggleExpand={() => handleSectionToggle('title')}
          />
          
          {/* Author Dropdown with Category Select */}
          <CategoryDropdown
            title="Browse by Author"
            filterType="author"
            onAddToLibrary={handleAddToLibrary}
            onReadNow={handleReadNow}
            isExpanded={expandedSection === 'author'}
            onToggleExpand={() => handleSectionToggle('author')}
          />
          
          {/* Genre Dropdown with Category Select */}
          <CategoryDropdown
            title="Browse by Genre"
            filterType="genre"
            onAddToLibrary={handleAddToLibrary}
            onReadNow={handleReadNow}
            isExpanded={expandedSection === 'genre'}
            onToggleExpand={() => handleSectionToggle('genre')}
          />
          
          {/* Theme Dropdown with Category Select */}
          <CategoryDropdown
            title="Browse by Theme"
            filterType="theme"
            onAddToLibrary={handleAddToLibrary}
            onReadNow={handleReadNow}
            isExpanded={expandedSection === 'theme'}
            onToggleExpand={() => handleSectionToggle('theme')}
          />
        </div>
      </main>

      {/* Universal Footer */}
      <UniversalFooter />

      {/* Reader Mobile Bottom Navigation */}
      <ReaderMobileBottomNav />

      {/* Continue Reading Modal */}
      <ContinueReadingModal 
        isOpen={showContinueReadingModal}
        onClose={() => setShowContinueReadingModal(false)}
        continueListeningBooks={continueListeningBooks}
        hasLastSession={hasLastSession}
        onResumeListening={handleResumeListening}
      />
      
      {/* Audia Help Modal */}
      <AskAudiaHelpModal 
        isOpen={showAudiaHelpModal}
        onClose={() => setShowAudiaHelpModal(false)}
        config={CONTEXT_CONFIG['reader-home']}
        context="reader-home"
      />
    </div>
  );
};

export default ReaderHomePage;
